import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PortfolioConsultationDialog } from "@/components/plans/PortfolioConsultationDialog";
import { usePlans } from "@/hooks/usePlans";
import { useQuickCheckout } from "@/hooks/useCheckout";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { BillingCycle, PlanDto } from "@/types/api";

interface RecommendationCardProps {
  recommendedSlug: string;
  cycle: BillingCycle;
  why: string;
}

export function RecommendationCard({ recommendedSlug, cycle, why }: RecommendationCardProps) {
  const { data, isLoading } = usePlans();
  const { toast } = useToast();
  const checkout = useQuickCheckout();
  const [consultOpen, setConsultOpen] = React.useState(false);

  const plan = (data ?? []).find(p => p.slug === recommendedSlug);

  if (isLoading) return <RecommendationSkeleton />;
  if (!plan) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Couldn't load the recommended plan. <Link to="/plans" className="text-primary hover:underline">Browse all plans</Link> instead.
        </p>
      </Card>
    );
  }

  const isCustom = !plan.twentyi_product_code;
  const price = priceFor(plan, cycle);

  async function onClaim() {
    if (isCustom) {
      setConsultOpen(true);
      return;
    }
    try {
      const result = await checkout.mutateAsync({
        plan_slug: plan!.slug,
        billing_cycle: cycle
      });
      window.location.href = result.redirect_url;
    } catch (err) {
      toast({
        title: "Couldn't start checkout",
        description: err instanceof ApiError ? err.message : "Try again.",
        variant: "destructive"
      });
    }
  }

  return (
    <>
      <Card className="relative overflow-hidden border-brand-accent p-6 shadow-brand-lg ring-2 ring-brand-accentLighter sm:p-8">
        <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-brand-accentSoft" aria-hidden />

        <div className="relative">
          <Badge variant="soft" className="mb-3 inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Recommended for you
          </Badge>

          <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{plan.name}</h3>
          {plan.description && (
            <p className="mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
              {plan.description}
            </p>
          )}

          <div className="mt-4 rounded-lg border border-brand-border bg-brand-tint p-3 text-sm">
            <p className="font-semibold text-brand-accentDark">Why this one?</p>
            <p className="mt-0.5 text-foreground">{why}</p>
          </div>

          <div className="mt-5 flex flex-wrap items-baseline gap-2">
            {isCustom ? (
              <span className="font-display text-3xl font-bold">Custom</span>
            ) : (
              <>
                <span className="font-display text-4xl font-bold tracking-tight">${Math.round(price)}</span>
                <span className="text-sm text-muted-foreground">/{cycleLabel(cycle)}</span>
              </>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              size="lg"
              onClick={onClaim}
              disabled={checkout.isPending}
              className="sm:flex-1"
            >
              {checkout.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isCustom ? "Talk to sales" : "Get started"}
              {!checkout.isPending && <ArrowRight className="h-4 w-4" />}
            </Button>
            <Button asChild variant="outline" size="lg" className="sm:w-auto">
              <Link to="/plans">Compare all plans</Link>
            </Button>
          </div>
        </div>
      </Card>

      <PortfolioConsultationDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        planSlug={plan.slug}
      />
    </>
  );
}

function priceFor(plan: PlanDto, cycle: BillingCycle): number {
  if (cycle === "annually")  return plan.price_annually  ?? plan.price_monthly * 12;
  if (cycle === "quarterly") return plan.price_quarterly ?? plan.price_monthly * 3;
  return plan.price_monthly;
}

function cycleLabel(cycle: BillingCycle): string {
  return cycle === "annually" ? "yr" : cycle === "quarterly" ? "qtr" : "mo";
}

function RecommendationSkeleton() {
  return (
    <Card className="p-6 sm:p-8">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="mt-3 h-8 w-48" />
      <Skeleton className="mt-2 h-4 w-3/4" />
      <Skeleton className="mt-4 h-16 w-full" />
      <Skeleton className="mt-5 h-10 w-32" />
      <Skeleton className="mt-6 h-11 w-full" />
    </Card>
  );
}
